import * as THREE from 'three';

/**
 * SOTA audio analysis pipeline for real-time visualization.
 *
 * Pipeline (CPU side, runs once per frame):
 *   1. Web Audio AnalyserNode → raw FFT (byte frequency data)
 *   2. Logarithmic band mapping (bass / mid / treble) — human hearing is
 *      logarithmic, so linear bin mapping produces jittery, unnatural motion.
 *   3. Exponential Moving Average (EMA) smoothing — raw FFT data is noisy;
 *      smoothing gives the animation "weight" and temporal coherence.
 *   4. Beat detection via adaptive thresholding on the smoothed bass envelope.
 *   5. Pack the full spectrum into a `THREE.DataTexture` and upload to the GPU.
 *      A single texture sampler replaces fixed-size uniform arrays (which hit
 *      WebGL uniform limits and are slow to update) — the shader can read
 *      thousands of bands instantly via `texture2D()`.
 *
 * The texture is 1D (height = 1) stored as RGBA: R = smoothed band value,
 * G = raw value, B = flux (change), A = 1. The vertex/fragment shaders sample
 * it by UV to drive GPU-side displacement and color.
 */

export interface AudioFeatures {
  /** Smoothed band levels, 0..1 */
  bass: number;
  mid: number;
  treble: number;
  /** Sub-bass (20–60 Hz) — felt more than heard, drives "depth/portals" */
  sub: number;
  /** Air (>8 kHz) — adds sparkle/shimmer */
  air: number;
  /** Overall loudness, 0..1 */
  level: number;
  /** True on the frame a beat is detected */
  isBeat: boolean;
  /** Decaying envelope (1 → 0) that spikes on each beat — drives "punch" */
  beatEnvelope: number;
  /** Raw spectrum length (== texture width) */
  bins: number;
  // ── 5D-ASC-dimensional features ──────────────────────────────────
  // Each maps an audio property onto a phenomenological dimension (Dittrich):
  /** Spectral centroid 0..1 — brightness → Audiovisual Synesthesia / color saturation */
  centroid: number;
  /** Spectral flux 0..1 — rate of spectral change → REBUS entropy / instability */
  flux: number;
  /** Spectral dissonance 0..1 — roughness → Dread of Egoic Dissolution (chaos/glitch) */
  dissonance: number;
  /** Bass:treble ratio → Oceanic Boundlessness (low-heavy = vast/submerged) */
  oceanic: number;
  /** Spectral spread/novelty → Visionary Restructuralization (geometric complexity) */
  restructuring: number;
  /** 1 - level → Reduced Vigilance (drift/trailing when energy is low) */
  vigilance: number;
}

const TEX_SIZE = 256;

export class AudioProcessor {
  private ctx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | MediaElementAudioSourceNode | null = null;
  private stream: MediaStream | null = null;
  private fileEl: HTMLAudioElement | null = null;

  private freqData: Uint8Array = new Uint8Array(0);
  private prevData: Uint8Array = new Uint8Array(0);

  // EMA-smoothed band envelopes
  private smoothBass = 0;
  private smoothMid = 0;
  private smoothTreble = 0;
  private smoothLevel = 0;
  // EMA-smoothed ASC-dimensional features
  private smoothSub = 0;
  private smoothAir = 0;
  private smoothCentroid = 0.5;
  private smoothFlux = 0;
  private smoothDissonance = 0;

  // Beat detection state
  private beatHistory: number[] = [];
  private beatCooldown = 0;
  private beatEnvelope = 0;
  isBeat = false;

  /** The GPU-readable spectrum texture. Updated every frame. */
  readonly texture: THREE.DataTexture;
  private texData: Uint8Array;

  active = false;
  sourceType: 'mic' | 'file' | null = null;

  constructor() {
    this.texData = new Uint8Array(TEX_SIZE * 4).fill(0);
    this.texture = new THREE.DataTexture(
      this.texData,
      TEX_SIZE,
      1,
      THREE.RGBAFormat,
    );
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.needsUpdate = true;
  }

  /** Start analysing microphone input. Must be called from a user gesture. */
  async startMic(): Promise<void> {
    this.stop();
    const AC = this.ctx = new AudioContext();
    this.analyser = AC.createAnalyser();
    this.analyser.fftSize = TEX_SIZE * 2;
    this.analyser.smoothingTimeConstant = 0.8;
    this.freqData = new Uint8Array(this.analyser.frequencyBinCount);
    this.prevData = new Uint8Array(this.analyser.frequencyBinCount);

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.stream = stream;
    this.source = AC.createMediaStreamSource(stream);
    this.source.connect(this.analyser);
    this.active = true;
    this.sourceType = 'mic';
  }

  /** Start analysing an uploaded audio file. Must be called from a user gesture. */
  async startFile(file: File): Promise<void> {
    this.stop();
    const AC = this.ctx = new AudioContext();
    if (AC.state === 'suspended') {
      try { await AC.resume(); } catch { /* ignore */ }
    }
    this.analyser = AC.createAnalyser();
    this.analyser.fftSize = TEX_SIZE * 2;
    this.analyser.smoothingTimeConstant = 0.8;
    this.freqData = new Uint8Array(this.analyser.frequencyBinCount);
    this.prevData = new Uint8Array(this.analyser.frequencyBinCount);

    const audio = new Audio();
    audio.src = URL.createObjectURL(file);
    audio.loop = true;
    audio.setAttribute('playsinline', 'true');
    this.fileEl = audio;

    // Build the graph before playback to avoid the default-output blip.
    this.source = AC.createMediaElementSource(audio);
    this.source.connect(this.analyser);
    this.analyser.connect(AC.destination);

    try {
      await audio.play();
    } catch (e) {
      throw new Error('Playback failed: ' + (e instanceof Error ? e.message : String(e)));
    }
    this.active = true;
    this.sourceType = 'file';
  }

  togglePlay(): void {
    if (this.fileEl) {
      if (this.fileEl.paused) void this.fileEl.play();
      else this.fileEl.pause();
    }
  }

  get isPlaying(): boolean {
    return !!this.fileEl && !this.fileEl.paused;
  }

  /** Tear down all audio resources. */
  stop(): void {
    if (this.fileEl) {
      this.fileEl.pause();
      this.fileEl.src = '';
      this.fileEl = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
    if (this.source) {
      try { this.source.disconnect(); } catch { /* ignore */ }
      this.source = null;
    }
    if (this.ctx && this.ctx.state !== 'closed') {
      this.ctx.close().catch(() => {});
    }
    this.ctx = null;
    this.analyser = null;
    this.active = false;
    this.sourceType = null;
    this.smoothBass = this.smoothMid = this.smoothTreble = this.smoothLevel = 0;
    this.smoothSub = this.smoothAir = 0;
    this.smoothCentroid = 0.5;
    this.smoothFlux = this.smoothDissonance = 0;
    this.beatEnvelope = 0;
    this.isBeat = false;
    this.beatHistory.length = 0;
    // Clear the texture so the visualizer falls back to idle motion.
    this.texData.fill(0);
    this.texture.needsUpdate = true;
  }

  /**
   * Advance one frame. Reads the analyser, smooths bands, runs beat detection,
   * and uploads a fresh spectrum DataTexture. Returns the current features.
   * When inactive, returns zeros and the visualizer idles gracefully.
   */
  update(): AudioFeatures {
    if (!this.analyser || !this.active) {
      // Decay envelopes toward zero so motion settles gently.
      this.beatEnvelope *= 0.92;
      this.isBeat = false;
      return {
        bass: 0, mid: 0, treble: 0, sub: 0, air: 0, level: 0,
        isBeat: false, beatEnvelope: this.beatEnvelope, bins: TEX_SIZE,
        centroid: 0.5, flux: 0, dissonance: 0, oceanic: 0, restructuring: 0, vigilance: 1,
      };
    }

    this.analyser.getByteFrequencyData(this.freqData);
    const n = this.freqData.length;

    // ── Logarithmic band extraction ──────────────────────────────
    // Map perceptual frequency ranges to FFT bins. Human hearing is
    // logarithmic, so we average bins over Hz ranges rather than raw indices.
    const sampleRate = this.ctx?.sampleRate ?? 44100;
    const binHz = sampleRate / this.analyser.fftSize;
    const idxOf = (hz: number) => Math.min(n - 1, Math.max(0, Math.round(hz / binHz)));
    const avg = (loHz: number, hiHz: number) => {
      const lo = idxOf(loHz);
      const hi = idxOf(hiHz);
      let sum = 0;
      for (let i = lo; i <= hi; i++) sum += this.freqData[i];
      const count = hi - lo + 1;
      return count > 0 ? sum / count / 255 : 0;
    };

    const rawSub = avg(20, 60);
    const rawBass = avg(60, 250);
    const rawMid = avg(250, 2000);
    const rawTreble = avg(2000, 8000);
    const rawAir = avg(8000, 16000);
    const rawLevel = (rawBass + rawMid + rawTreble) / 3;

    // ── EMA smoothing (temporal coherence) ───────────────────────
    // Bass is snappy (fast attacks), treble/mid are smoother.
    this.smoothSub += (rawSub - this.smoothSub) * 0.40;
    this.smoothBass += (rawBass - this.smoothBass) * 0.35;
    this.smoothMid += (rawMid - this.smoothMid) * 0.20;
    this.smoothTreble += (rawTreble - this.smoothTreble) * 0.12;
    this.smoothAir += (rawAir - this.smoothAir) * 0.15;
    this.smoothLevel += (rawLevel - this.smoothLevel) * 0.25;

    // ── Spectral centroid (brightness → synesthesia/color) ───────
    let cNum = 0, cDen = 0;
    for (let i = 0; i < n; i++) { cNum += this.freqData[i] * i; cDen += this.freqData[i]; }
    const rawCentroid = cDen > 0 ? cNum / cDen / n : 0.5;
    this.smoothCentroid += (rawCentroid - this.smoothCentroid) * 0.12;

    // ── Spectral flux (onset energy → REBUS entropy) ─────────────
    let fluxSum = 0;
    for (let i = 0; i < n; i++) {
      const d = this.freqData[i] - this.prevData[i];
      if (d > 0) fluxSum += d;
    }
    const rawFlux = Math.min(1, fluxSum / (n * 255));
    this.smoothFlux += (rawFlux - this.smoothFlux) * 0.25;

    // ── Spectral dissonance (roughness → ego-dissolution dread) ──
    // Approximate sensory roughness: sum of beating interactions between
    // adjacent critical-band bins (Sethares-style simplification).
    let diss = 0;
    for (let i = 2; i < n - 2; i++) {
      const a = this.freqData[i] / 255;
      const b = this.freqData[i + 2] / 255; // ~minor-second beating
      diss += a * b * (a + b) * 0.5;
    }
    const rawDissonance = Math.min(1, diss / n);
    this.smoothDissonance += (rawDissonance - this.smoothDissonance) * 0.1;

    // ── Beat detection (adaptive threshold on bass) ──────────────
    this.beatHistory.push(this.smoothBass);
    if (this.beatHistory.length > 43) this.beatHistory.shift(); // ~1s @ 60fps
    const histAvg =
      this.beatHistory.reduce((a, b) => a + b, 0) / Math.max(this.beatHistory.length, 1);

    this.isBeat = false;
    if (this.beatCooldown <= 0 && this.smoothBass > histAvg * 1.35 + 0.05) {
      this.isBeat = true;
      this.beatEnvelope = 1;
      this.beatCooldown = 8; // frames — prevent double-triggers
    } else {
      this.beatCooldown--;
    }
    // Exponential decay of the beat envelope.
    this.beatEnvelope *= 0.90;

    // ── Build the spectrum DataTexture ───────────────────────────
    // R = raw band, G = flux (transient), B = (centroid bit-packed),
    // A = dissonance. The shader samples this 1D texture by UV.
    for (let i = 0; i < TEX_SIZE; i++) {
      // Sample logarithmically so low freqs get more texels (perceptual).
      const t = i / TEX_SIZE;
      const binIdx = Math.min(n - 1, Math.floor(Math.pow(t, 2) * n));
      const raw = this.freqData[binIdx] ?? 0;
      const prev = this.prevData[binIdx] ?? 0;
      const flux = Math.max(0, raw - prev);
      const o = i * 4;
      this.texData[o] = raw;
      this.texData[o + 1] = Math.min(255, flux * 4);
      // Encode centroid as a slow horizontal gradient overlay.
      this.texData[o + 2] = Math.round(this.smoothCentroid * 255 * t);
      this.texData[o + 3] = Math.round(this.smoothDissonance * 255);
      this.prevData[binIdx] = raw;
    }
    this.texture.needsUpdate = true;

    // ── 5D-ASC dimensional mapping ───────────────────────────────
    const oceanic = Math.min(1, (this.smoothSub * 1.5 + this.smoothBass) / (this.smoothTreble + 0.3));
    const restructuring = Math.min(1, this.smoothMid * 0.6 + this.smoothFlux * 0.8 + this.beatEnvelope * 0.4);
    const vigilance = Math.max(0, 1 - this.smoothLevel * 1.5);

    return {
      bass: this.smoothBass,
      mid: this.smoothMid,
      treble: this.smoothTreble,
      sub: this.smoothSub,
      air: this.smoothAir,
      level: this.smoothLevel,
      isBeat: this.isBeat,
      beatEnvelope: this.beatEnvelope,
      bins: TEX_SIZE,
      centroid: this.smoothCentroid,
      flux: this.smoothFlux,
      dissonance: this.smoothDissonance,
      oceanic,
      restructuring,
      vigilance,
    };
  }
}
